/**
 * AiTeamPage — AI 员工团队管理
 *
 * Two-tier AI Employee management:
 * 1. Team Overview — org chart grouped by department + role
 * 2. Employee Detail — skills, channels, metrics, customization
 * 3. Deploy — deploy templates from Boox Network to tenant
 *
 * Data from:
 *   GET  /api/v1/ai-employees              — list tenant employees
 *   GET  /api/v1/ai-employees/team-overview — dashboard summary
 *   GET  /api/v1/ai-employees/:id           — detail + metrics
 *   PATCH /api/v1/ai-employees/:id          — customize
 *   PATCH /api/v1/ai-employees/:id/status   — activate/suspend
 *   PATCH /api/v1/ai-employees/:id/skills/:sid — toggle/config skill
 *   GET  /api/v1/ai-employees/templates     — available templates
 *   POST /api/v1/ai-employees/deploy        — deploy template
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users,
  UserPlus,
  RefreshCw,
  Loader2,
  Bot,
  Play,
  Pause,
  Settings,
  Zap,
  Mail,
  MessageCircle,
  Webhook,
  Globe,
  Smartphone,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Shield,
  BarChart3,
  Target,
  DollarSign,
  Star,
  Truck,
  Package,
  Receipt,
  FileText,
  Save,
  RotateCcw,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ─── Types ─────────────────────────────────────────

interface AiEmployee {
  id: string;
  tenant_id: string;
  template_slug: string;
  deployment_id: string;
  employee_code: string;
  name: string;
  name_zh: string;
  role: string;
  department: string;
  avatar_url: string | null;
  status: 'active' | 'idle' | 'suspended' | 'training' | 'error';
  execution_mode: string;
  system_prompt_override: string | null;
  personality: Record<string, unknown> | null;
  working_hours: Record<string, unknown> | null;
  reports_to: string | null;
  total_goals: number;
  completed_goals: number;
  failed_goals: number;
  avg_response_ms: number;
  satisfaction_score: number;
  created_at: string;
  updated_at: string;
  skills?: AiEmployeeSkill[];
  channels?: AiEmployeeChannel[];
}

interface AiEmployeeSkill {
  id: string;
  employee_id: string;
  skill_id: string;
  enabled: boolean;
  priority: number;
  config: Record<string, unknown>;
}

interface AiEmployeeChannel {
  id: string;
  employee_id: string;
  channel_type: 'email' | 'whatsapp' | 'webhook' | 'web_chat' | 'sms';
  channel_address: string;
  direction: 'inbound' | 'outbound' | 'both';
}

interface AiEmployeeMetric {
  metric_date: string;
  goals_created: number;
  goals_completed: number;
  goals_failed: number;
  goals_escalated: number;
  avg_response_ms: number;
  total_tokens: number;
  total_cost_usd: number;
  customer_satisfaction: number;
}

interface Template {
  id: string;
  slug: string;
  name: string;
  name_zh: string;
  role: string;
  department: string;
  description: string;
  description_zh: string;
  version: string;
  active: boolean;
  skills?: { skill_id: string; priority: number; required: boolean }[];
}

interface TeamOverview {
  total: number;
  byStatus: Record<string, number>;
  byDepartment: Record<string, { total: number; active: number; employees: AiEmployee[] }>;
  byRole: Record<string, { total: number; active: number }>;
}

type ViewMode = 'team' | 'detail' | 'deploy';

interface HierarchyBootstrapResponse {
  tenantId: string;
  appliedGraphTemplate: 'decision_intelligence';
  graph: {
    nodes: number;
    edges: number;
  };
  deployments: {
    created: string[];
    reused: string[];
  };
  employees: {
    created: string[];
    updated: string[];
    items: AiEmployee[];
  };
}

interface HandoffPacket {
  id: string;
  from_role: string;
  to_role: string;
  job_id: string | null;
  reason_code: string | null;
  reason_detail: string | null;
  priority: string | null;
  status: string;
  context_summary: string | Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface HandoffSummary {
  trigger?: unknown;
  chiefEmployeeCode?: unknown;
  deputyEmployeeCode?: unknown;
  selectedAgentId?: unknown;
  chatSpace?: unknown;
  pageContext?: unknown;
  messagePreview?: unknown;
}

interface HandoffListResponse {
  items: HandoffPacket[];
  total: number;
  page: number;
  limit: number;
}

// ─── Helper maps ───────────────────────────────────

const STATUS_CONFIG: Record<string, { color: string; icon: typeof CheckCircle; label: string }> = {
  active: { color: 'bg-green-500', icon: CheckCircle, label: 'Active' },
  idle: { color: 'bg-yellow-500', icon: Clock, label: 'Idle' },
  suspended: { color: 'bg-red-500', icon: Pause, label: 'Suspended' },
  training: { color: 'bg-blue-500', icon: TrendingUp, label: 'Training' },
  error: { color: 'bg-red-600', icon: XCircle, label: 'Error' },
};

const ROLE_CONFIG: Record<string, { icon: typeof Bot; label: string; color: string }> = {
  customer_service: { icon: MessageCircle, label: 'Customer Service', color: 'text-blue-600' },
  operations: { icon: Truck, label: 'Operations', color: 'text-orange-600' },
  finance: { icon: Receipt, label: 'Finance', color: 'text-green-600' },
  supervisor: { icon: Shield, label: 'Supervisor', color: 'text-purple-600' },
  chief: { icon: Target, label: 'Chief', color: 'text-fuchsia-600' },
  deputy: { icon: Zap, label: 'Deputy', color: 'text-amber-600' },
  router: { icon: ChevronRight, label: 'Router', color: 'text-sky-600' },
  planner: { icon: FileText, label: 'Planner', color: 'text-slate-600' },
  guardian: { icon: Shield, label: 'Guardian', color: 'text-rose-600' },
  revenue: { icon: DollarSign, label: 'Revenue', color: 'text-emerald-600' },
  maintenance: { icon: Settings, label: 'Maintenance', color: 'text-zinc-600' },
  builder: { icon: Zap, label: 'Builder', color: 'text-violet-600' },
  growth: { icon: TrendingUp, label: 'Growth', color: 'text-lime-600' },
  compliance: { icon: Shield, label: 'Compliance', color: 'text-red-600' },
  customs: { icon: Shield, label: 'Customs', color: 'text-cyan-700' },
  data_analyst: { icon: BarChart3, label: 'Data Analyst', color: 'text-indigo-600' },
};

const CHANNEL_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  whatsapp: MessageCircle,
  webhook: Webhook,
  web_chat: Globe,
  sms: Smartphone,
};

const DEPT_ICONS: Record<string, typeof Truck> = {
  transportation: Truck,
  warehouse: Package,
  freight: Globe,
  shipping: Globe,
  finance: DollarSign,
  general: Bot,
  compliance: Shield,
  platform: Settings,
  'business-development': TrendingUp,
  'customer-facing': MessageCircle,
};

// ─── Component ─────────────────────────────────────

export default function AiTeamPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState<ViewMode>('team');
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<AiEmployee[]>([]);
  const [overview, setOverview] = useState<TeamOverview | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [hierarchyHandoffs, setHierarchyHandoffs] = useState<HandoffPacket[]>([]);
  const [hierarchyHandoffTotal, setHierarchyHandoffTotal] = useState(0);
  const [expandedHandoffId, setExpandedHandoffId] = useState<string | null>(null);
  const [handoffChainByJobId, setHandoffChainByJobId] = useState<Record<string, HandoffPacket[]>>({});
  const [handoffChainLoadingJobId, setHandoffChainLoadingJobId] = useState<string | null>(null);

  // Detail view state
  const [selectedEmployee, setSelectedEmployee] = useState<AiEmployee | null>(null);
  const [metrics, setMetrics] = useState<AiEmployeeMetric[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Deploy view state
  const [deploySlug, setDeploySlug] = useState('');
  const [deployCode, setDeployCode] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [bootstrappingHierarchy, setBootstrappingHierarchy] = useState(false);

  // Prompt editing state
  const [promptDraft, setPromptDraft] = useState('');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptDirty, setPromptDirty] = useState(false);

  // ─── Data Fetching ─────────────────────────────

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, overviewRes, handoffRes] = await Promise.allSettled([
        api.get<{ items: AiEmployee[] }>('/ai-employees'),
        api.get<TeamOverview>('/ai-employees/team-overview'),
        api.get<HandoffListResponse>('/handoff-packets?from_role=chief&to_role=deputy&reason_code=front_door_chat&limit=8'),
      ]);
      if (empRes.status === 'fulfilled') setEmployees(empRes.value.items ?? []);
      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value);
      if (handoffRes.status === 'fulfilled') {
        setHierarchyHandoffs(handoffRes.value.items ?? []);
        setHierarchyHandoffTotal(handoffRes.value.total ?? 0);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      const [empRes, metricsRes] = await Promise.allSettled([
        api.get<AiEmployee>(`/ai-employees/${id}`),
        api.get<{ items: AiEmployeeMetric[] }>(`/ai-employees/${id}/metrics?days=30`),
      ]);
      if (empRes.status === 'fulfilled') {
        setSelectedEmployee(empRes.value);
        if (!promptDirty) {
          setPromptDraft(empRes.value.system_prompt_override ?? '');
        }
      }
      if (metricsRes.status === 'fulfilled') setMetrics(metricsRes.value.items ?? []);
    } catch {
      // ignore
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get<{ items: Template[] }>('/ai-employees/templates');
      setTemplates(res.items ?? []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    const expanded = hierarchyHandoffs.find((handoff) => handoff.id === expandedHandoffId);
    const jobId = expanded?.job_id;
    if (!jobId || handoffChainByJobId[jobId]) return;

    let cancelled = false;
    setHandoffChainLoadingJobId(jobId);

    api.get<HandoffListResponse>(`/handoff-packets?job_id=${encodeURIComponent(jobId)}&limit=50`)
      .then((res) => {
        if (cancelled) return;
        setHandoffChainByJobId((current) => ({ ...current, [jobId]: res.items ?? [] }));
      })
      .catch(() => {
        if (cancelled) return;
        setHandoffChainByJobId((current) => ({ ...current, [jobId]: [] }));
      })
      .finally(() => {
        if (!cancelled) setHandoffChainLoadingJobId((current) => current === jobId ? null : current);
      });

    return () => {
      cancelled = true;
    };
  }, [expandedHandoffId, handoffChainByJobId, hierarchyHandoffs]);

  // ─── Actions ───────────────────────────────────

  const toggleStatus = async (emp: AiEmployee) => {
    const next = emp.status === 'active' ? 'suspended' : 'active';
    setActionLoading(emp.id);
    try {
      await api.patch(`/ai-employees/${emp.id}/status`, { status: next });
      await fetchTeam();
      if (selectedEmployee?.id === emp.id) {
        await fetchDetail(emp.id);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSkill = async (employeeId: string, skillId: string, enabled: boolean) => {
    setActionLoading(skillId);
    try {
      await api.patch(`/ai-employees/${employeeId}/skills/${skillId}`, { enabled: !enabled });
      await fetchDetail(employeeId);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeploy = async () => {
    if (!deploySlug || !deployCode) return;
    setDeploying(true);
    try {
      await api.post('/ai-employees/deploy', {
        templateSlug: deploySlug,
        employeeCode: deployCode,
      });
      toast.success('AI employee deployed');
      setView('team');
      setDeploySlug('');
      setDeployCode('');
      await fetchTeam();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to deploy AI employee');
    } finally {
      setDeploying(false);
    }
  };

  const handleBootstrapHierarchy = async () => {
    setBootstrappingHierarchy(true);
    try {
      const result = await api.post<HierarchyBootstrapResponse>('/ai-employees/bootstrap-hierarchy', {});
      const total = result.employees.items.length;
      const created = result.employees.created.length;
      const updated = result.employees.updated.length;
      toast.success(`Hierarchy ready: ${total} members (${created} created, ${updated} updated)`);
      setView('team');
      await fetchTeam();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to bootstrap hierarchy');
    } finally {
      setBootstrappingHierarchy(false);
    }
  };

  const savePromptOverride = async () => {
    if (!selectedEmployee) return;
    setPromptSaving(true);
    try {
      const value = promptDraft.trim() || null;
      await api.patch(`/ai-employees/${selectedEmployee.id}`, {
        systemPromptOverride: value,
      });
      setSelectedEmployee({ ...selectedEmployee, system_prompt_override: value });
      setPromptDirty(false);
    } finally {
      setPromptSaving(false);
    }
  };

  const openDetail = (emp: AiEmployee) => {
    setSelectedEmployee(emp);
    setPromptDraft(emp.system_prompt_override ?? '');
    setPromptDirty(false);
    setView('detail');
    fetchDetail(emp.id);
  };

  const openDeploy = () => {
    setView('deploy');
    fetchTemplates();
  };

  // ─── Render: Team Overview ─────────────────────

  const renderTeamView = () => {
    const grouped = new Map<string, AiEmployee[]>();
    for (const emp of employees) {
      const key = emp.department || 'general';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(emp);
    }
    const hasHierarchy = employees.some((emp) =>
      emp.role === 'chief' || emp.role === 'deputy' || emp.role === 'router',
    );

    return (
      <div className="space-y-6">
        <Card className={cn('border-dashed', hasHierarchy ? 'border-emerald-200 bg-emerald-50/40' : 'border-primary/30 bg-primary/5')}>
          <CardContent className="pt-5 pb-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Target className={cn('h-4 w-4', hasHierarchy ? 'text-emerald-600' : 'text-primary')} />
                  <p className="text-sm font-semibold">
                    {hasHierarchy ? 'Hierarchy Blueprint 已启用' : 'Recommended: Bootstrap Default Hierarchy'}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  一键应用 `decision_intelligence` 组织图，并部署 `Chief / Deputy / Router / Customer / Ops / Finance` 默认团队。
                </p>
              </div>
              <Button
                onClick={handleBootstrapHierarchy}
                disabled={bootstrappingHierarchy}
                variant={hasHierarchy ? 'outline' : 'default'}
              >
                {bootstrappingHierarchy ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Target className="h-4 w-4 mr-2" />
                )}
                {hasHierarchy ? 'Re-apply Hierarchy' : 'Bootstrap Hierarchy'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5 text-amber-600" />
              Hierarchy Activity
              <Badge variant="secondary" className="ml-auto">
                {hierarchyHandoffTotal} handoffs
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Front-door delegation:</span>
              <Badge variant="outline">Chief</Badge>
              <ChevronRight className="h-3 w-3" />
              <Badge variant="outline">Deputy</Badge>
              <span>· 最近自动编排轨迹</span>
            </div>

            {hierarchyHandoffs.length > 0 ? (
              <div className="space-y-2">
                {hierarchyHandoffs.map((handoff) => (
                  <HierarchyHandoffRow
                    key={handoff.id}
                    handoff={handoff}
                    expanded={expandedHandoffId === handoff.id}
                    onToggle={() => setExpandedHandoffId((current) => current === handoff.id ? null : handoff.id)}
                    onOpenConversation={handoff.job_id ? () => navigate(`/chat/${handoff.job_id}`) : undefined}
                    chain={handoff.job_id ? handoffChainByJobId[handoff.job_id] : undefined}
                    chainLoading={handoff.job_id != null && handoffChainLoadingJobId === handoff.job_id}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                {hasHierarchy
                  ? 'Hierarchy 已启用，但最近还没有记录到 Chief -> Deputy handoff。发起一次普通 chat 后，这里会出现最新 delegation。'
                  : '先 bootstrap hierarchy，再从普通 chat 入口发一条消息，这里会显示 Chief -> Deputy 的自动 delegation。'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard
              icon={Users}
              label="Team Members"
              value={overview.total}
              sub={`${overview.byStatus?.active ?? 0} Active`}
              color="text-primary"
            />
            <SummaryCard
              icon={Target}
              label="Completed Today"
              value={employees.reduce((s, e) => s + e.completed_goals, 0)}
              sub={`${employees.reduce((s, e) => s + e.failed_goals, 0)} Failed`}
              color="text-green-600"
            />
            <SummaryCard
              icon={Clock}
              label="Avg Response"
              value={`${Math.round(employees.reduce((s, e) => s + e.avg_response_ms, 0) / Math.max(employees.length, 1))}ms`}
              sub="Full Team"
              color="text-orange-600"
            />
            <SummaryCard
              icon={Star}
              label="Satisfaction"
              value={`${(employees.reduce((s, e) => s + Number(e.satisfaction_score ?? 0), 0) / Math.max(employees.length, 1)).toFixed(1)}`}
              sub="Avg Rating"
              color="text-purple-600"
            />
          </div>
        )}

        {/* Department Groups */}
        {Array.from(grouped.entries()).map(([dept, emps]) => {
          const DeptIcon = DEPT_ICONS[dept] ?? Bot;
          return (
            <Card key={dept}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <DeptIcon className="h-5 w-5" />
                  {t(`department.${dept}`, dept === 'transportation' ? 'Transportation' : dept)}
                  <Badge variant="secondary" className="ml-auto">
                    {emps.length} members
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {emps.map((emp) => (
                    <EmployeeCard
                      key={emp.id}
                      employee={emp}
                      onOpen={() => openDetail(emp)}
                      onToggle={() => toggleStatus(emp)}
                      loading={actionLoading === emp.id}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {employees.length === 0 && !loading && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Bot className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-2">No AI Employees Yet</p>
              <p className="text-sm mb-4">Start with the default hierarchy, or deploy a single employee manually.</p>
              <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
                <Button onClick={handleBootstrapHierarchy} disabled={bootstrappingHierarchy}>
                  {bootstrappingHierarchy ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Target className="h-4 w-4 mr-2" />
                  )}
                  Bootstrap Hierarchy
                </Button>
                <Button variant="outline" onClick={openDeploy}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Deploy AI Employee
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ─── Render: Employee Detail ───────────────────

  const renderDetailView = () => {
    if (!selectedEmployee) return null;
    const emp = selectedEmployee;
    const roleConf = ROLE_CONFIG[emp.role];
    const statusConf = STATUS_CONFIG[emp.status];
    const RoleIcon = roleConf?.icon ?? Bot;
    const StatusIcon = statusConf?.icon ?? Clock;

    const successRate =
      emp.total_goals > 0 ? ((emp.completed_goals / emp.total_goals) * 100).toFixed(1) : '—';

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setView('team')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Team
          </Button>
        </div>

        {/* Profile Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted text-3xl">
                {emp.avatar_url ? (
                  <img src={emp.avatar_url} alt="" className="h-full w-full rounded-2xl object-cover" />
                ) : (
                  <RoleIcon className={cn('h-8 w-8', roleConf?.color)} />
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold">{emp.name_zh || emp.name}</h2>
                  <Badge variant={emp.status === 'active' ? 'default' : 'secondary'} className="gap-1">
                    <StatusIcon className="h-3 w-3" />
                    {statusConf?.label ?? emp.status}
                  </Badge>
                  <Badge variant="outline">{emp.employee_code}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {roleConf?.label ?? emp.role} · {emp.department} · {emp.execution_mode}
                </p>
                {emp.name_zh && emp.name !== emp.name_zh && (
                  <p className="text-xs text-muted-foreground mt-0.5">{emp.name}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant={emp.status === 'active' ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => toggleStatus(emp)}
                  disabled={actionLoading === emp.id}
                >
                  {actionLoading === emp.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : emp.status === 'active' ? (
                    <>
                      <Pause className="h-4 w-4 mr-1" /> Suspend
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-1" /> Activate
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Tasks" value={emp.total_goals} icon={Target} />
          <StatCard label="Completed" value={emp.completed_goals} icon={CheckCircle} color="text-green-600" />
          <StatCard label="Failed" value={emp.failed_goals} icon={XCircle} color="text-red-600" />
          <StatCard label="Success Rate" value={`${successRate}%`} icon={TrendingUp} color="text-primary" />
          <StatCard label="Satisfaction" value={emp.satisfaction_score != null ? Number(emp.satisfaction_score).toFixed(1) : '—'} icon={Star} color="text-yellow-600" />
        </div>

        {/* System Prompt Override */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5" />
              System Prompt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-2">
              自定义 AI 员工的系统提示词。保存后即时生效，无需重部署。留空则使用默认提示词。
            </p>
            <Textarea
              value={promptDraft}
              onChange={(e) => { setPromptDraft(e.target.value); setPromptDirty(true); }}
              placeholder="输入自定义系统提示词…"
              className="min-h-[160px] font-mono text-sm"
            />
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={savePromptOverride}
                disabled={promptSaving || !promptDirty}
              >
                {promptSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                保存
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPromptDraft(emp.system_prompt_override ?? ''); setPromptDirty(false); }}
                disabled={!promptDirty}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                重置
              </Button>
              {emp.system_prompt_override && (
                <Badge variant="secondary" className="text-[10px] ml-auto">
                  已自定义
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skills */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5" />
              Skills ({emp.skills?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emp.skills && emp.skills.length > 0 ? (
              <div className="space-y-2">
                {emp.skills.map((skill) => (
                  <div
                    key={skill.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <Zap className={cn('h-4 w-4', skill.enabled ? 'text-green-500' : 'text-muted-foreground')} />
                      <div>
                        <p className="text-sm font-medium">{skill.skill_id}</p>
                        <p className="text-xs text-muted-foreground">Priority: {skill.priority}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSkill(emp.id, skill.skill_id, skill.enabled)}
                      disabled={actionLoading === skill.skill_id}
                    >
                      {actionLoading === skill.skill_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : skill.enabled ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-200">Enabled</Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No skills assigned</p>
            )}
          </CardContent>
        </Card>

        {/* Channels */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5" />
              Channels ({emp.channels?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emp.channels && emp.channels.length > 0 ? (
              <div className="space-y-2">
                {emp.channels.map((ch) => {
                  const ChIcon = CHANNEL_ICONS[ch.channel_type] ?? Globe;
                  return (
                    <div key={ch.id} className="flex items-center gap-3 rounded-lg border p-3">
                      <ChIcon className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{ch.channel_type}</p>
                        <p className="text-xs text-muted-foreground">{ch.channel_address}</p>
                      </div>
                      <Badge variant="outline">{ch.direction}</Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No channels configured</p>
            )}
          </CardContent>
        </Card>

        {/* 30-day Metrics */}
        {metrics.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                30-day Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b">
                      <th className="text-left py-2 pr-4">Date</th>
                      <th className="text-right py-2 px-2">Done</th>
                      <th className="text-right py-2 px-2">Failed</th>
                      <th className="text-right py-2 px-2">Escalated</th>
                      <th className="text-right py-2 px-2">Resp(ms)</th>
                      <th className="text-right py-2 px-2">Token</th>
                      <th className="text-right py-2 px-2">Cost($)</th>
                      <th className="text-right py-2 pl-2">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.slice(0, 14).map((m) => (
                      <tr key={m.metric_date} className="border-b last:border-0">
                        <td className="py-1.5 pr-4 font-mono">{m.metric_date.slice(5)}</td>
                        <td className="text-right px-2 text-green-600">{m.goals_completed}</td>
                        <td className="text-right px-2 text-red-600">{m.goals_failed}</td>
                        <td className="text-right px-2 text-yellow-600">{m.goals_escalated}</td>
                        <td className="text-right px-2">{m.avg_response_ms}</td>
                        <td className="text-right px-2">{m.total_tokens.toLocaleString()}</td>
                        <td className="text-right px-2">{Number(m.total_cost_usd).toFixed(2)}</td>
                        <td className="text-right pl-2">{Number(m.customer_satisfaction).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ─── Render: Deploy View ───────────────────────

  const renderDeployView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => setView('team')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Team
        </Button>
        <h2 className="text-lg font-semibold">Deploy AI Employee</h2>
      </div>

      <Card className="border-dashed border-primary/30 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-5 w-5" />
            Default Team Bootstrap
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-muted-foreground">
            如果你要的是完整 `Chief / Deputy / Router / Specialists` 组织模型，不要一个个部署，直接用 bootstrap。
          </div>
          <Button onClick={handleBootstrapHierarchy} disabled={bootstrappingHierarchy}>
            {bootstrappingHierarchy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Target className="h-4 w-4 mr-2" />
            )}
            Bootstrap Hierarchy
          </Button>
        </CardContent>
      </Card>

      {/* Template Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Select Template</CardTitle>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No templates available</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {templates.map((tpl) => {
                const roleConf = ROLE_CONFIG[tpl.role];
                const RoleIcon = roleConf?.icon ?? Bot;
                const isSelected = deploySlug === tpl.slug;
                return (
                  <button
                    key={tpl.slug}
                    onClick={() => setDeploySlug(tpl.slug)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'hover:bg-muted/50',
                    )}
                  >
                    <RoleIcon className={cn('h-6 w-6 mt-0.5', roleConf?.color)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tpl.name_zh || tpl.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{tpl.description_zh || tpl.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px]">{tpl.role}</Badge>
                        <Badge variant="outline" className="text-[10px]">{tpl.department}</Badge>
                        <Badge variant="secondary" className="text-[10px]">v{tpl.version}</Badge>
                      </div>
                      {tpl.skills && tpl.skills.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tpl.skills.map((s) => (
                            <Badge key={s.skill_id} variant="secondary" className="text-[10px]">
                              <Zap className="h-2.5 w-2.5 mr-0.5" />
                              {s.skill_id}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {isSelected && <CheckCircle className="h-5 w-5 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deploy Config */}
      {deploySlug && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Deploy Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Employee Code</label>
              <Input
                value={deployCode}
                onChange={(e) => setDeployCode(e.target.value)}
                placeholder="e.g. CST-001"
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">Unique identifier within tenant</p>
            </div>
            <Button onClick={handleDeploy} disabled={!deployCode || deploying}>
              {deploying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Deploy to Current Tenant
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ─── Main Render ───────────────────────────────

  return (
    <div className="flex-1 overflow-auto p-6">
      {/* Page Header */}
      {view === 'team' && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />
              AI Employee Team
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage AI team members · Deploy, Configure, Monitor
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchTeam} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button size="sm" onClick={openDeploy}>
              <UserPlus className="h-4 w-4 mr-2" />
              Deploy New Employee
            </Button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && view === 'team' && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Views */}
      {!loading && view === 'team' && renderTeamView()}
      {view === 'detail' && renderDetailView()}
      {view === 'deploy' && renderDeployView()}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function SummaryCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  sub: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          <Icon className={cn('h-5 w-5', color)} />
          <span className="text-2xl font-bold">{value}</span>
        </div>
        <p className="text-sm font-medium mt-1">{label}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: typeof Target;
  color?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-3 pb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', color ?? 'text-muted-foreground')} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className="text-xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

function EmployeeCard({
  employee,
  onOpen,
  onToggle,
  loading,
}: {
  employee: AiEmployee;
  onOpen: () => void;
  onToggle: () => void;
  loading: boolean;
}) {
  const roleConf = ROLE_CONFIG[employee.role];
  const statusConf = STATUS_CONFIG[employee.status];
  const RoleIcon = roleConf?.icon ?? Bot;
  const StatusIcon = statusConf?.icon ?? Clock;

  const skillCount = employee.skills?.filter((s) => s.enabled).length ?? 0;
  const channelCount = employee.channels?.length ?? 0;

  return (
    <div className="rounded-lg border p-4 hover:shadow-sm transition-shadow">
      {/* Top row: avatar + name + status */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
          <RoleIcon className={cn('h-5 w-5', roleConf?.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold truncate">{employee.name_zh || employee.name}</p>
            <div className={cn('h-2 w-2 rounded-full', statusConf?.color)} title={statusConf?.label} />
          </div>
          <p className="text-xs text-muted-foreground">{employee.employee_code} · {roleConf?.label ?? employee.role}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Target className="h-3 w-3" />
          {employee.completed_goals}/{employee.total_goals}
        </span>
        <span className="flex items-center gap-1">
          <Zap className="h-3 w-3" />
          {skillCount} skills
        </span>
        <span className="flex items-center gap-1">
          <Mail className="h-3 w-3" />
          {channelCount} channels
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3">
        <Button variant="outline" size="sm" className="flex-1 text-xs h-7" onClick={onOpen}>
          <Settings className="h-3 w-3 mr-1" />
          Manage
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onToggle}
          disabled={loading}
          title={employee.status === 'active' ? 'Suspend' : 'Activate'}
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : employee.status === 'active' ? (
            <Pause className="h-3 w-3" />
          ) : (
            <Play className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}

function HierarchyHandoffRow({
  handoff,
  expanded,
  onToggle,
  onOpenConversation,
  chain,
  chainLoading,
}: {
  handoff: HandoffPacket;
  expanded: boolean;
  onToggle: () => void;
  onOpenConversation?: () => void;
  chain?: HandoffPacket[];
  chainLoading: boolean;
}) {
  const summary = parseContextSummary(handoff.context_summary);
  const messagePreview = getString(summary.messagePreview);
  const chiefEmployeeCode = getString(summary.chiefEmployeeCode);
  const deputyEmployeeCode = getString(summary.deputyEmployeeCode);
  const chatSpace = getString(summary.chatSpace);
  const selectedAgentId = getString(summary.selectedAgentId);
  const statusTone = getHandoffStatusTone(handoff.status);
  const pageContext = getRecord(summary.pageContext);

  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <Badge variant="outline">{handoff.from_role}</Badge>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <Badge variant="outline">{handoff.to_role}</Badge>
            <Badge className={statusTone}>{handoff.status}</Badge>
            {handoff.priority && (
              <Badge variant="secondary">{handoff.priority}</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {handoff.reason_detail ?? handoff.reason_code ?? 'Delegation recorded'}
          </p>
          {(chiefEmployeeCode || deputyEmployeeCode || chatSpace) && (
            <p className="text-xs text-muted-foreground">
              {chiefEmployeeCode ? `Chief: ${chiefEmployeeCode}` : 'Chief: —'}
              {' · '}
              {deputyEmployeeCode ? `Deputy: ${deputyEmployeeCode}` : 'Deputy: —'}
              {chatSpace ? ` · Space: ${chatSpace}` : ''}
            </p>
          )}
          {messagePreview && (
            <p className="text-xs rounded-md bg-muted px-2 py-1 font-mono text-muted-foreground line-clamp-2">
              {messagePreview}
            </p>
          )}
        </div>

        <div className="shrink-0 text-right text-xs text-muted-foreground">
          <p>{formatTimestamp(handoff.created_at)}</p>
          {handoff.job_id && <p className="mt-1 font-mono">job: {handoff.job_id}</p>}
          <div className="mt-2 flex justify-end gap-1">
            {onOpenConversation && (
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={onOpenConversation}>
                Open Conversation
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onToggle}>
              <ChevronDown className={cn('h-3.5 w-3.5 mr-1 transition-transform', expanded && 'rotate-180')} />
              {expanded ? 'Hide Detail' : 'View Detail'}
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 rounded-lg bg-muted/50 p-3 text-xs">
          <div className="grid gap-2 md:grid-cols-2">
            <DetailItem label="Handoff ID" value={handoff.id} mono />
            <DetailItem label="Reason Code" value={handoff.reason_code ?? '—'} mono />
            <DetailItem label="Selected Agent" value={selectedAgentId ?? '—'} mono />
            <DetailItem label="Trigger" value={getString(summary.trigger) ?? '—'} />
            <DetailItem label="Chat Space" value={chatSpace ?? '—'} />
            <DetailItem label="Updated At" value={formatTimestamp(handoff.updated_at)} />
          </div>

          {pageContext && Object.keys(pageContext).length > 0 && (
            <div className="mt-3">
              <p className="mb-1 font-medium text-foreground">Page Context</p>
              <pre className="overflow-x-auto rounded-md bg-background px-3 py-2 font-mono text-[11px] text-muted-foreground">
                {JSON.stringify(pageContext, null, 2)}
              </pre>
            </div>
          )}

          <div className="mt-3">
            <p className="mb-1 font-medium text-foreground">Delegation Chain</p>
            {chainLoading ? (
              <div className="flex items-center gap-2 rounded-md bg-background px-3 py-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading handoff chain…
              </div>
            ) : chain && chain.length > 0 ? (
              <div className="space-y-2">
                {chain
                  .slice()
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                  .map((item) => (
                    <ChainStep key={item.id} handoff={item} active={item.id === handoff.id} />
                  ))}
              </div>
            ) : (
              <div className="rounded-md bg-background px-3 py-2 text-muted-foreground">
                No additional handoffs recorded for this conversation yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function parseContextSummary(
  input: HandoffPacket['context_summary'],
): HandoffSummary {
  if (!input) return {};
  if (typeof input === 'object') return input;
  if (typeof input !== 'string') return {};
  try {
    const parsed = JSON.parse(input) as unknown;
    return parsed && typeof parsed === 'object' ? parsed as HandoffSummary : {};
  } catch {
    return {};
  }
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getHandoffStatusTone(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200';
    case 'open':
      return 'bg-amber-100 text-amber-700 hover:bg-amber-200';
    case 'failed':
      return 'bg-red-100 text-red-700 hover:bg-red-200';
    default:
      return 'bg-slate-100 text-slate-700 hover:bg-slate-200';
  }
}

function ChainStep({
  handoff,
  active,
}: {
  handoff: HandoffPacket;
  active: boolean;
}) {
  return (
    <div className={cn(
      'flex items-center gap-2 rounded-md border px-3 py-2',
      active ? 'border-primary bg-background' : 'border-border/60 bg-background/70',
    )}>
      <Badge variant="outline">{handoff.from_role}</Badge>
      <ChevronRight className="h-3 w-3 text-muted-foreground" />
      <Badge variant="outline">{handoff.to_role}</Badge>
      <span className="truncate text-muted-foreground">
        {handoff.reason_code ?? handoff.reason_detail ?? 'handoff'}
      </span>
      <Badge className={cn('ml-auto', getHandoffStatusTone(handoff.status))}>{handoff.status}</Badge>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className={cn('mt-0.5 text-foreground', mono && 'font-mono')}>{value}</p>
    </div>
  );
}
